import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { GraphQLResponse, ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    BankAccountType,
    GetInvoiceSummaryForSellerRequest
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { InvoiceSummary2, PayablesResponse, Query, TransferType } from '../../model/models';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import {
    mapPayableStatusLegacyToEarlyPmtStatus,
    mapPayableStatusLegacyToInvoiceFundingStatus
} from '../../helpers/status-mapping';
import { convertDateToLocalDate } from '../../helpers/date-mapping';

export class GetInvoiceSummaryForSellerResponse {
    public ok = false;
    public invoiceSummary = new InvoiceSummary2();
    public error: Error = null;
    public isBuyerPaidEarlyFeePercentageVisible = false;
    public buyerPaidEarlyPaymentFeePercentage = 0;

    public constructor(init?: Partial<GetInvoiceSummaryForSellerResponse>) {
        Object.keys(init ?? {}).forEach((k) => (this[k] = init[k]));
    }
}

@Injectable({ providedIn: 'root' })
export class GetInvoiceSummaryForSellerService extends ProtoApiService<
    GetInvoiceSummaryForSellerRequest,
    GetInvoiceSummaryForSellerResponse
> {
    public requestType = GetInvoiceSummaryForSellerRequest;
    public responseType = GetInvoiceSummaryForSellerResponse;
    public endpointName = 'GetInvoiceSummaryForSeller';
    public serviceName = 'financing';

    private _payLaterIsOn = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._payLaterIsOn = isOn;
                if (isOn) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'GetInvoiceSummaryForSeller';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    // TODO: Implement `useScheduledDate` flag. This is used when we want to display the estimated
    // fees for a scheduled funding. Best way to implement is probably not to use a flag, but to
    // add a field called `estimatedSupplierDiscountFee` and consume it wherever `useScheduledDate`
    // is being passed as `true`.
    public requestMapper(
        req: GetInvoiceSummaryForSellerRequest
    ): { query: string } | GetInvoiceSummaryForSellerRequest {
        if (this._payLaterIsOn) {
            return {
                query: `{${jsonToGraphQLQuery({
                    payables: {
                        __args: {
                            input: {
                                payableIds: [+req.refInvoiceKey],
                                companyFilter: { supplierIds: [req.refSellerKey] }
                            }
                        },
                        rows: {
                            name: true,
                            statusLegacy: true,
                            isGroup: true,
                            invoiceDateParent: true,
                            fundingDate: true,
                            originalDueDate: true,
                            buyerDiscountFeeRate: true,
                            buyerDiscountFee: { cents: true, currencyCode: true },
                            supplierDiscountFeeRate: true,
                            supplierDiscountFee: { cents: true, currencyCode: true },
                            relationship: {
                                buyer: { company: { name: true } },
                                scfConfig: {
                                    isBuyerPaidEarlyPaymentFeePercentageVisible: true
                                }
                            },
                            approvals: {
                                id: true,
                                amount: { cents: true, currencyCode: true },
                                invoice: {
                                    invoiceNumber: true
                                }
                            },
                            transfers: {
                                payeeBankAccount: {
                                    accountNumber: true,
                                    bankName: true,
                                    accountType: true
                                }
                            }
                        }
                    }
                } as Query)}}`
            };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | GetInvoiceSummaryForSellerResponse
    ): GetInvoiceSummaryForSellerResponse {
        if (this._payLaterIsOn) {
            const payable = (res as GraphQLResponse<PayablesResponse>).data.payables.rows[0];
            const approval = payable.approvals[0];
            const { buyer } = payable.relationship;
            const { scfConfig } = payable.relationship;

            const invoiceDate = convertDateToLocalDate(payable.invoiceDateParent);
            const invoiceDueDate = convertDateToLocalDate(payable.originalDueDate);
            const fundingDate = convertDateToLocalDate(payable.fundingDate);

            const transfer = payable.transfers[0];
            const bankAccountType = this._translateBankAccountType(
                BankAccountType[transfer?.payeeBankAccount.accountType]
            );
            const paymentMethod =
                transfer &&
                `${transfer.payeeBankAccount.bankName} (${this._obfuscateAccountNumber(
                    transfer.payeeBankAccount.accountNumber
                )})`;

            return new GetInvoiceSummaryForSellerResponse({
                // TODO: Add fields
                ok: true,
                isBuyerPaidEarlyFeePercentageVisible:
                    scfConfig?.isBuyerPaidEarlyPaymentFeePercentageVisible || false,
                invoiceSummary: new InvoiceSummary2({
                    fundingDate,
                    invoiceDate,
                    invoiceDueDate,
                    groupId: payable.isGroup && payable.name,
                    refGroupedInvoiceKey: approval.id.toString(),
                    buyerName: buyer.company.name,
                    invoiceNumber: approval.invoice.invoiceNumber,
                    finalApprovedAmount: approval.amount,
                    discountAmount: payable.supplierDiscountFee,
                    availableAmount: payable.supplierDiscountFee,
                    invoiceStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                        payable.statusLegacy
                    ),
                    earlyPaymentStatus: mapPayableStatusLegacyToEarlyPmtStatus(
                        payable.statusLegacy
                    ),
                    feeRateHundredthOfBps: payable.supplierDiscountFeeRate * 100,
                    buyerDiscountAmount: payable.buyerDiscountFee,
                    supplierDiscountAmount: payable.supplierDiscountFee,
                    // TODO: Legacy: If the invoice is unfunded, then buyerDiscountPercentage = buyerPaidEarlyPaymentFeePercentage
                    buyerDiscountPercentage: payable.buyerDiscountFeeRate * 100,
                    bankAccountType,
                    paymentMethod
                })
            });
        }
        return res as GetInvoiceSummaryForSellerResponse;
    }

    public get endpointUrl(): string {
        return this._payLaterIsOn ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }

    // TODO: translate to proper status enum
    private _translateBankAccountType(transferType?: TransferType): BankAccountType {
        return BankAccountType[transferType] || BankAccountType.UNKNOWN_BANK_ACCOUNT_TYPE;
    }

    private _obfuscateAccountNumber(accountNumber: string): string {
        return accountNumber.slice(-3).padStart(accountNumber.length, '*');
    }
}
